/* ──────────────────────────────────────────────────────────────────────
 *  DimensionMedal — Hexagonal embossed medal for one dimension/tier pair
 *
 *  Renders a single SVG medal carrying:
 *    - Outer hexagon with metallic gradient (rim → base → highlight)
 *    - Recessed inner field (darker metal tone)
 *    - Top-edge specular highlight (subtle catch of light)
 *    - Embossed dimension icon stamped into the field (Lucide path data
 *      from the same icons the rest of the app uses for these dimensions)
 *
 *  Sizes are driven by the parent CSS — the SVG fills its container.
 *  Tier 'none' renders a hollow placeholder medal so the slot keeps
 *  reserving the same space (no layout jump when a tier is earned).
 *
 *  Follows CLAUDE.md conventions:
 *    - No raw colors in JSX; metallic tones are deliberately literal
 *      because they're a visual design language tied to medal tiers
 *      (not theme colors that re-skin between dark/light).
 *    - No user-facing strings — all labels are rendered by the caller.
 * ────────────────────────────────────────────────────────────────────── */

import React, { useId } from 'react';
import type { DimKey } from './dimensionConfig';

export type TierLevel = 'none' | 'bronze' | 'silver' | 'gold' | 'platinum';

/* ── Medal metallic palette ──
   Pulled from the existing badge_bronze/silver/gold gradients in
   DimensionRadar.module.css so the new embossed medals stay in
   the same visual language as the small hex badges they replace.

   Each tier has five tones:
     rim       — darkest, used for the hex outline and stroke
     base      — the canonical "this is bronze/silver/etc" mid-tone
     hi        — lightest, used for the icon stamp and specular catch
     field     — slightly darker than base, fills the recessed center
     fieldDark — yet darker, sits at the top of the field gradient        */
const MEDAL_TONES: Record<Exclude<TierLevel, 'none'>, {
  rim: string;
  base: string;
  hi: string;
  field: string;
  fieldDark: string;
}> = {
  bronze: {
    rim: '#5D3F1F',
    base: '#CD7F32',
    hi: '#FFE5BD',
    field: '#8B5E34',
    fieldDark: '#704624',
  },
  silver: {
    rim: '#586470',
    base: '#A8B4C0',
    hi: '#F0F4F8',
    field: '#7A8490',
    fieldDark: '#646E78',
  },
  gold: {
    rim: '#5C4D14',
    base: '#D4AF37',
    hi: '#FFF4B0',
    field: '#B8962E',
    fieldDark: '#937322',
  },
  platinum: {
    rim: '#5D6F81',
    base: '#C8D6E2',
    hi: '#F8FBFF',
    field: '#A8B8C4',
    fieldDark: '#92A3B1',
  },
};

/* ── Lucide icon path data ──
   Pulled directly from the lucide-react package — these are the exact
   icons mapped in src/Components/DimensionRadar/dimensionConfig.ts
   (Brain, Volume2, Heart, User, Bird, TreeDeciduous). Stored as raw
   SVG element strings because Lucide React components render as
   <svg> wrappers, which cannot nest inside our medal SVG. We render
   the path data via dangerouslySetInnerHTML on inner <g> wrappers —
   path data is platform-controlled and contains no user input. */
const ICON_PATHS: Record<DimKey, string> = {
  mind: `
    <path d="M12 4.5a2.5 2.5 0 0 0-4.96-.46 2.5 2.5 0 0 0-1.98 3 2.5 2.5 0 0 0-1.32 4.24 3 3 0 0 0 .34 5.58 2.5 2.5 0 0 0 2.96 3.08A2.5 2.5 0 0 0 9.5 22c1.21 0 2.5-.5 2.5-2.5v-15"/>
    <path d="M12 4.5a2.5 2.5 0 0 1 4.96-.46 2.5 2.5 0 0 1 1.98 3 2.5 2.5 0 0 1 1.32 4.24 3 3 0 0 1-.34 5.58 2.5 2.5 0 0 1-2.96 3.08A2.5 2.5 0 0 1 14.5 22c-1.21 0-2.5-.5-2.5-2.5v-15"/>
  `,
  voice: `
    <path d="M11 5 6 9H2v6h4l5 4z"/>
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
  `,
  heart: `
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
  `,
  body: `
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  `,
  wings: `
    <path d="M16 7h.01"/>
    <path d="M3.4 18H12a8 8 0 0 0 8-8V7a4 4 0 0 0-7.28-2.3L2 20"/>
    <path d="m20 7 2 .5-2 .5"/>
    <path d="M10 18v3"/>
    <path d="M14 17.75V21"/>
    <path d="M7 18a6 6 0 0 0 3.84-10.61"/>
  `,
  roots: `
    <path d="M8 19a4 4 0 0 1-2.24-7.32A3.5 3.5 0 0 1 9 6.03V6a3 3 0 1 1 6 0v.04a3.5 3.5 0 0 1 3.24 5.65A4 4 0 0 1 16 19Z"/>
    <path d="M12 19v3"/>
  `,
};

/* ── Hex polygon geometry ──
   ViewBox is 100 × 116 — a pointy-top hexagon at 100 wide is roughly
   116 tall (height = width × 2/√3 ≈ 1.155 × 100, rounded). Inner hex
   is pulled in by ~5 units on each side — slim bevel, large field. */
const OUTER_HEX = '50,4 94,30 94,82 50,108 6,82 6,30';
const INNER_HEX = '50,11 89,32 89,80 50,101 11,80 11,32';
const HIGHLIGHT = '50,4 94,30 94,38 50,14 6,38 6,30';

/* Icon group transform: place icon center at (50, 56) and scale 2.5×
   so the 24×24 Lucide viewBox occupies 60×60 of the medal — about 75%
   of the inner field's width. */
const ICON_SCALE = 2.5;
const ICON_TX = 50 - 12 * ICON_SCALE;
const ICON_TY = 56 - 12 * ICON_SCALE;

/* ── Props ── */
interface DimensionMedalProps {
  /** Tier level for this dimension. 'none' renders an empty placeholder. */
  tier: TierLevel;
  /** Which dimension's icon to stamp into the medal field. */
  dim: DimKey;
  /** Optional aria-label; defaults to a descriptive composite. */
  ariaLabel?: string;
}

/* ── Component ── */
export default function DimensionMedal({ tier, dim, ariaLabel }: DimensionMedalProps) {
  /* useId gives us a stable, collision-free suffix for the gradient
     IDs so multiple medals on the same page never share IDs. Without
     this, two medals with the same tier/dim combo would alias their
     gradients to the first one rendered. */
  const uid = useId().replace(/[:]/g, '');

  if (tier === 'none') {
    return (
      <svg
        viewBox="0 0 100 116"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid meet"
        aria-label={ariaLabel ?? `${dim} — not yet earned`}
        role="img"
        style={{ width: '100%', height: '100%', display: 'block' }}
      >
        <polygon
          points={OUTER_HEX}
          fill="none"
          stroke="var(--border-default)"
          strokeWidth={0.8}
          strokeDasharray="2 1.5"
        />
        <polygon
          points={INNER_HEX}
          fill="var(--bg-surface, rgba(0,0,0,0.15))"
          opacity={0.5}
        />
        <text
          x={50}
          y={62}
          textAnchor="middle"
          fill="var(--text-muted)"
          fontSize={20}
          fontWeight={700}
        >
          —
        </text>
      </svg>
    );
  }

  const t = MEDAL_TONES[tier];
  const rimId = `medal-rim-${uid}`;
  const fieldId = `medal-field-${uid}`;

  return (
    <svg
      viewBox="0 0 100 116"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
      aria-label={ariaLabel ?? `${dim} — ${tier}`}
      role="img"
      style={{ width: '100%', height: '100%', display: 'block' }}
    >
      <defs>
        <linearGradient id={rimId} x1="0" y1="0" x2="0.7" y2="1">
          <stop offset="0" stopColor={t.hi} />
          <stop offset="0.5" stopColor={t.base} />
          <stop offset="1" stopColor={t.rim} />
        </linearGradient>
        <linearGradient id={fieldId} x1="0.3" y1="0" x2="0.7" y2="1">
          <stop offset="0" stopColor={t.fieldDark} />
          <stop offset="1" stopColor={t.field} />
        </linearGradient>
      </defs>

      {/* Outer body — metallic gradient */}
      <polygon
        points={OUTER_HEX}
        fill={`url(#${rimId})`}
        stroke={t.rim}
        strokeWidth={0.6}
      />

      {/* Recessed inner field */}
      <polygon points={INNER_HEX} fill={`url(#${fieldId})`} />

      {/* Top-edge specular highlight */}
      <polygon points={HIGHLIGHT} fill="rgba(255,255,255,0.18)" />

      {/* Embossed icon — paired strokes (dark shadow + light highlight)
          for the stamped/recessed look. Stroke-width 0.9 in user units
          renders at ~2.25px at our typical card medal size, matching
          Lucide's default 2px stroke weight closely. */}
      <g
        transform={`translate(${ICON_TX}, ${ICON_TY}) scale(${ICON_SCALE})`}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <g
          stroke="rgba(0,0,0,0.55)"
          strokeWidth={0.9}
          transform="translate(0, 0.45)"
          dangerouslySetInnerHTML={{ __html: ICON_PATHS[dim] }}
        />
        <g
          stroke={t.hi}
          strokeWidth={0.9}
          dangerouslySetInnerHTML={{ __html: ICON_PATHS[dim] }}
        />
      </g>
    </svg>
  );
}
